import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAsync } from '@/hooks/useAsync';
import { getCurrentGameweek, getManagerSummary } from '@/services';
import { useAuthStore } from '@/stores/auth.store';
import type { Gameweek, ManagerSummary } from '@/types';

export interface AppDataContextValue {
  gameweek: Gameweek | null;
  summary: ManagerSummary | null;
  isLoading: boolean;
  /**
   * Per-source loading flags, for screens that render the two independently —
   * a failed or empty gameweek must not hold the standings on a skeleton.
   */
  isGameweekLoading: boolean;
  isSummaryLoading: boolean;
  error: Error | null;
  /** Re-fetch standings after a squad change alters them. */
  refreshSummary: () => void;
}

export const AppDataContext = createContext<AppDataContextValue | null>(null);

/**
 * Loads the data every authenticated screen depends on: the current gameweek
 * (deadline, lock state) and the manager's standings.
 *
 * Also reconciles the squad — against the server when one is configured, and
 * against the player pool otherwise — so no screen renders a squad that
 * references players who no longer exist.
 */
export function AppDataProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const restoreSession = useAuthStore((s) => s.restoreSession);

  /**
   * Confirm the session against the API once, as soon as there is one.
   *
   * The stored profile is enough to render with, but it says nothing about
   * whether the session behind it still stands — the token was written by an
   * earlier visit and may since have been revoked or aged past refreshing, and
   * only the API can say. Asking here ends a dead session at the front door
   * instead of letting every screen discover it separately.
   */
  const confirmed = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      // A later sign-in is a different session and gets its own check.
      confirmed.current = false;
      return;
    }

    if (confirmed.current) return;
    confirmed.current = true;

    void restoreSession();
  }, [isAuthenticated, restoreSession]);

  // Bumping this re-runs the summary fetch.
  const [summaryNonce, setSummaryNonce] = useState(0);

  const gameweek = useAsync(
    (signal) => (isAuthenticated ? getCurrentGameweek(signal) : Promise.resolve(null)),
    [isAuthenticated],
  );

  const summary = useAsync(
    (signal) => (isAuthenticated ? getManagerSummary(signal) : Promise.resolve(null)),
    [isAuthenticated, summaryNonce],
  );


  const refreshSummary = useCallback(() => setSummaryNonce((n) => n + 1), []);

  const value = useMemo<AppDataContextValue>(
    () => ({
      gameweek: gameweek.data,
      summary: summary.data,
      isLoading: gameweek.isLoading || summary.isLoading,
      isGameweekLoading: gameweek.isLoading,
      isSummaryLoading: summary.isLoading,
      error: gameweek.error ?? summary.error,
      refreshSummary,
    }),
    [gameweek, summary, refreshSummary],
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}
